import React, { useEffect, useState } from 'react'
import {
  DialogHeader,
  DialogWindow,
  DialogAdornment,
  useDialog,
  Button,
  Alert,
  Avatar,
} from '@ticketswap/solar'
import { CloseAlt } from '@ticketswap/comets'
import { WidePageLayout } from '~/components/PageLayout'
import { space, color, Input } from '@ticketswap/solar'
import styled from '@emotion/styled'
import { useMutation } from '@apollo/client'
import SEND_POTENTIAL_PARTNER_EMAIL from '~/graphql/mutations/sendPotentialPartnerEmail'
import { validateEmail } from '~/utils'
import PropTypes from 'prop-types'

const StyledWidePageLayout = styled(WidePageLayout)`
  margin: ${space[32]} 0;
`

const Employee = styled.div`
  display: flex;
  flex-flow: space-between;
  align-items: center;
  margin-bottom: ${space[24]};
`

const StyledAvatar = styled(Avatar)`
  margin-right: ${space[16]};
`

const EmployeeText = styled.p`
  color: ${color.spaceMedium};
`

const StyledInput = styled(Input)`
  margin-bottom: ${space[16]};
`

const StyledButton = styled(Button)`
  margin-top: ${space[16]};
`

const PartnerDialog = ({ t, children }) => {
  const [isValidForm, setIsValidForm] = useState(true)
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sendEmail, { data = {}, loading }] = useMutation(SEND_POTENTIAL_PARTNER_EMAIL)
  const { sendPotentialPartnerEmail = {} } = data
  const { errors = [] } = sendPotentialPartnerEmail

  const clearForm = () => {
    setName('')
    setCompany('')
    setEmail('')
    setMessage('')
    setIsValidForm(false)
  }

  const { show, hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: () => {
      clearForm()
    },
  })

  useEffect(() => {
    validateEmail(email).then(validEmail =>
      setIsValidForm(Boolean(name && company && validEmail && message))
    )
  }, [name, company, email, message, setIsValidForm])

  const handleSubmit = async event => {
    event.preventDefault()

    const {
      data: {
        sendPotentialPartnerEmail: { errors },
      },
    } = await sendEmail({
      variables: {
        name,
        email,
        company,
        message,
      },
    })

    if (errors.length === 0) {
      closeForm()
    }
  }

  const closeForm = () => {
    hide()
    clearForm()
  }

  return (
    <>
      {children({ show, getToggleProps })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {t('partners:become.title')}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <StyledWidePageLayout>
          <Employee>
            <StyledAvatar src="/static/images/partners/jason.jpg" alt="Jason" size={64} />
            <EmployeeText>{t('partners:become.text')}</EmployeeText>
          </Employee>
          <form onSubmit={handleSubmit}>
            <StyledInput
              id="name"
              label={t('partners:become.form.name')}
              onChange={event => setName(event.currentTarget.value)}
              required={true}
            />
            <StyledInput
              id="company"
              label={t('partners:become.form.company')}
              onChange={event => setCompany(event.currentTarget.value)}
              required={true}
            />
            <StyledInput
              id="email"
              label={t('partners:become.form.email')}
              onChange={event => setEmail(event.currentTarget.value)}
              required={true}
              type="email"
            />
            <StyledInput
              id="message"
              label={t('partners:become.form.message')}
              onChange={event => setMessage(event.currentTarget.value)}
              as="textarea"
              rows="4"
            />
            {Boolean(errors.length) && <Alert variant="error">{errors[0].message}</Alert>}
            <StyledButton fullWidth type="submit" disabled={!isValidForm} loading={loading}>
              {t('partners:become.form.send')}
            </StyledButton>
          </form>
        </StyledWidePageLayout>
      </DialogWindow>
    </>
  )
}

PartnerDialog.propTypes = {
  t: PropTypes.func.isRequired,
}

export default PartnerDialog
